import { AfterViewInit, Directive, ElementRef, HostBinding, Input } from '@angular/core';
import { OnChange } from '@df/ng/on-change';

/**
 * Used with `grid` directive
 *
 * @example
 * ```html
 * <div grid="4">
 *   <div gridColumn="1"></div>
 *   <div gridColumn="2"></div>
 *   <div gridColumn="1"></div>
 * </div>
 *
 * // or
 * <div grid="4">
 *   <div></div> <!-- default column span is 1 -->
 *   <div gridColumn gridColumnSpan="2"></div>
 *   <div></div>
 * </div>
 * ```
 */
@Directive({
    selector: 'grid-column, [gridColumn]',
    standalone: true
})
export class GridColumnDirective implements AfterViewInit {
    /** Stretch column */
    @Input('gridColumn')
    @OnChange<number>(function (this: GridColumnDirective, value: number) {
        this.gridColumnSpan = value;
    })
    gridColumn?: number;

    /** Stretch column */
    @Input('gridColumnSpan')
    @HostBinding('style.--grid-column-span')
    gridColumnSpan?: number;

    /** Stretch row */
    @Input('gridRowSpan')
    @HostBinding('style.--grid-row-span')
    gridRowSpan?: number;

    /** Move column */
    @Input('gridColumnOffset')
    @OnChange<number>('updateColumnOffset')
    gridColumnOffset?: number;

    /** Push column to the first position in row  */
    @Input('gridColumnPushLeft')
    @OnChange<number>('updateColumnOffset')
    gridColumnPushLeft?: number;

    /** Push column to the last position in row  */
    @Input('gridColumnPushRight')
    @OnChange<number>('updateColumnOffset')
    gridColumnPushRight?: number;

    @HostBinding('style.grid-column-start')
    styleGridColumnStart = 'span var(--grid-column-span, 1)';

    @HostBinding('style.grid-column-end')
    styleGridColumnEnd?: string;

    @HostBinding('style.grid-row-start')
    styleGridRowStart = 'span var(--grid-row-span, 1)';

    @HostBinding('style.--grid-column-offset')
    styleGridColumnOffset?: number;

    @HostBinding('style.min-width')
    styleMinWidth = '0';

    constructor(protected elementRef: ElementRef) {}

    ngAfterViewInit() {
        this.update();
    }

    get element() {
        return this.elementRef.nativeElement;
    }

    update() {
        this.updateColumnOffset();
    }

    protected updateColumnOffset() {
        if (this.gridColumnOffset! >= 0) {
            const offset = this.gridColumnOffset! + 1;
            this.styleGridColumnOffset = offset;
            this.styleGridColumnStart = this.gridColumnPushLeft ? 'column-start' : 'var(--grid-column-offset, 1)';
            this.styleGridColumnEnd = this.gridColumnPushRight ? 'column-end' : 'span var(--grid-column-span, 1)';
        } else {
            this.styleGridColumnStart = this.gridColumnPushLeft ? 'column-start' : 'span var(--grid-column-span, 1)';
            this.styleGridColumnOffset = undefined;
            this.styleGridColumnEnd = this.gridColumnPushRight
                ? 'column-end'
                : this.gridColumnPushLeft
                  ? 'span var(--grid-column-span, 1)'
                  : undefined;
        }
    }
}
